import React from 'react';
import { CheckCircle2, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { trackReportClick } from '../lib/gtag';
import { useAudience } from '../contexts/AudienceContext';
import { solutionSectionContent } from '../data/audienceContent';
import { BrandName } from './BrandName';

const SolutionSection = ({ onGetStarted, onBookDemo }) => {
  const { audience } = useAudience();
  const copy = solutionSectionContent[audience];
  const isSchool = audience === 'school';

  const handlePrimary = () => {
    if (!isSchool) trackReportClick();
    if (isSchool && onBookDemo) onBookDemo();
    else onGetStarted();
  };

  return (
    <section className="py-24 bg-gradient-to-br from-blue-50 via-white to-teal-50">
      <div className="container mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
          <div className="relative order-2 lg:order-1">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={copy.imageSrc}
                alt={copy.imageAlt}
                width={800}
                height={600}
                loading="lazy"
                decoding="async"
                className="w-full h-auto object-cover min-h-[260px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-teal-900/20 to-transparent" />
            </div>

            <div className="absolute -top-6 -right-6 bg-white p-4 rounded-2xl shadow-xl border border-blue-100">
              <div className="flex items-center gap-3">
                <Sparkles className="w-8 h-8 text-teal-600 shrink-0" />
                <div>
                  <div className="text-sm font-medium text-gray-600">Powered by</div>
                  <div className="text-lg font-bold text-gray-900">AI + Science</div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-8 order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 bg-teal-100 px-4 py-2 rounded-full">
              <span className="text-sm font-semibold text-teal-900">
                The <BrandName /> Solution
              </span>
            </div>

            <h2 className="font-display text-4xl lg:text-5xl font-bold text-gray-900 leading-tight tracking-tight">
              {copy.headline}{' '}
              <span className="bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">
                {copy.headlineGradient}
              </span>
            </h2>

            <p className="text-xl text-gray-600 leading-relaxed">
              <BrandName /> {copy.lead}
            </p>

            <div className="space-y-4">
              {copy.bullets.map((solution, index) => (
                <div key={index} className="flex items-start gap-4 group">
                  <div className="flex-shrink-0 w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center mt-1 group-hover:bg-teal-200 transition-colors duration-300">
                    <CheckCircle2 className="w-4 h-4 text-teal-700" />
                  </div>
                  <p className="text-lg text-gray-700 leading-relaxed">{solution}</p>
                </div>
              ))}
            </div>

            <div className="pt-6">
              <Button
                onClick={handlePrimary}
                size="lg"
                className="bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-lg px-8 py-6"
              >
                {copy.ctaLabel}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;
